import type { StoryFormatId } from './storyFormats';
import { storyThemeIdOf, type StoryFrame } from './storyFrames';

/**
 * The look of each Story part per release theme (the CSS of design `asset-redesign/StoryFrame` and
 * `studio-details/ImageFrame`), as plain style objects. The renderer (music24-api `KitStory`) and
 * the web preview (music24-app `StoryPreview`) both spread them into their elements, so a preview
 * in the Studio looks like the rendered file. Every size is in the canvas of the format; `unit` is
 * the detail factor of the format (`StoryFormat.unit`: 1 for a Story, 0.88 for a feed post). Pure.
 */

/** A frame detail of the Story times the factor of the format, in whole px. */
const u = (value: number, unit: number): number => Math.round(value * unit);

/** A style object: camel-case CSS properties, as React `style` takes them. */
export type StoryCss = Record<string, string | number | undefined>;

/** The colours a Story reads: `themeColours` of the theme and the cover (the renderer's `ReelColors`). */
export interface StoryColours {
  /** The background of the theme. */
  ink: string;
  surface: string;
  text: string;
  accent: string;
  onAccent: string;
}

/** `#rrggbb` with an alpha, as `rgba(…)`. An invalid colour gives transparent black. */
export function storyAlpha(hex: string, alpha: number): string {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (match === null) {
    return `rgba(0, 0, 0, ${alpha})`;
  }
  const [r, g, b] = [match[1], match[2], match[3]].map((part) => parseInt(part ?? '0', 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** `#rrggbb` mix of two colours: `share` of `a`, the rest of `b` (CSS `color-mix(in srgb, a share, b)`). */
export function mixHex(a: string, b: string, share: number): string {
  const channel = (hex: string, index: number): number => {
    const value = parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);
    return Number.isNaN(value) ? 0 : value;
  };
  const value = (index: number): string =>
    Math.round(channel(a, index) * share + channel(b, index) * (1 - share))
      .toString(16)
      .padStart(2, '0');
  return `#${value(0)}${value(1)}${value(2)}`;
}

/** The dots of a grain or a paper ground (design: a 1 px dot on a 6 to 9 px grid). */
export function groundDots(colour: string, size: number, dot: number): string {
  return dots(colour, size, dot);
}

function dots(colour: string, size: number, dot: number): string {
  return `radial-gradient(${colour} ${dot}px, transparent ${dot + 0.5}px) 0 0 / ${size}px ${size}px`;
}

/** The ground of each theme (`.st-ground`), over the background colour. Null: a flat background (Clean). */
export function storyGroundBackground(themeId: string, colors: StoryColours): string | null {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return `radial-gradient(ellipse 70% 40% at 50% 40%, ${storyAlpha(colors.accent, 0.22)}, transparent 70%)`;
    case 'dreamy':
      return [
        'radial-gradient(ellipse 80% 30% at 50% 0%, rgba(255,255,255,.55), transparent 70%)',
        `radial-gradient(ellipse 90% 40% at 50% 100%, ${storyAlpha(colors.accent, 0.25)}, transparent 70%)`,
        `linear-gradient(180deg, ${colors.ink} 0%, ${colors.surface} 100%)`,
      ].join(', ');
    case 'dark':
      return `${dots('rgba(255,255,255,.07)', 7, 1)}, radial-gradient(ellipse 60% 40% at 50% 35%, ${colors.surface}, transparent 75%)`;
    case 'warm':
      return `radial-gradient(ellipse 80% 45% at 50% 22%, ${storyAlpha(colors.accent, 0.3)}, transparent 70%), linear-gradient(180deg, ${colors.surface} 0%, ${colors.ink} 60%)`;
    case 'retro':
      return dots('rgba(255,255,255,.08)', 6, 1);
    case 'neon':
      return `radial-gradient(ellipse 70% 35% at 15% 8%, ${storyAlpha(colors.text, 0.3)}, transparent 70%), radial-gradient(ellipse 80% 35% at 85% 96%, ${storyAlpha(colors.accent, 0.28)}, transparent 70%)`;
    case 'handmade':
      return `${dots('rgba(80,50,30,.05)', 9, 1)}, radial-gradient(ellipse 90% 60% at 50% 40%, rgba(255,255,255,.5), transparent 75%)`;
    case 'clean':
      return null;
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The opacity of the theme ground on the Cover ground style (`.y-ground .st-ground`). */
export const STORY_GROUND_ON_BLUR_OPACITY = 0.55;
/** The scrim over the blurred cover of the Cover ground style (`.st-scrim`: the background at 0.62). */
export const STORY_BLUR_SCRIM_OPACITY = 0.62;
/** The blurred cover of the Cover ground style (`.st-blur`): 12 % past each edge, blur 60 px, saturate 1.25. */
export const STORY_BLUR: StoryCss = {
  position: 'absolute',
  left: '-12%',
  top: '-12%',
  width: '124%',
  height: '124%',
  objectFit: 'cover',
  filter: 'blur(60px) saturate(1.25)',
};

/** The torn edge of the Handmade strip (`.t-handmade .d1`). */
const TORN_STRIP_PATH =
  'polygon(0 12%, 6% 0, 13% 10%, 21% 2%, 30% 12%, 39% 3%, 48% 11%, 57% 1%, 66% 10%, 75% 2%, 84% 12%, 93% 3%, 100% 9%, 100% 88%, 92% 100%, 83% 90%, 74% 99%, 65% 89%, 55% 98%, 46% 90%, 37% 100%, 28% 91%, 18% 99%, 9% 90%, 0 98%)';

const stripe = (style: StoryCss): StoryCss => ({ position: 'absolute', left: '-10%', right: '-10%', ...style });
const arc = (top: number, background: string, height = 1200): StoryCss => ({
  position: 'absolute',
  left: '-30%',
  right: '-30%',
  top,
  height,
  borderRadius: '50%',
  background,
});

/** The torn top edge of the Handmade strip of a feed post: it rises from the bottom edge. */
export const TORN_TOP_PATH =
  'polygon(0 12%, 6% 0, 13% 10%, 21% 2%, 30% 12%, 39% 3%, 48% 11%, 57% 1%, 66% 10%, 75% 2%, 84% 12%, 93% 3%, 100% 9%, 100% 100%, 0 100%)';

/**
 * The ornaments of a feed post (`FeedFrame.dc.html`, `.fp-deco`): they live in the 72 px margins
 * and at the edges. Hype: stripes across the top-left and bottom-right corners. Retro: three arcs
 * rise from the bottom edge (y 1296 to 1350). Handmade: the torn strip at the bottom edge.
 */
function feedDeco(themeId: string, colors: StoryColours): StoryCss[] {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return [
        stripe({ top: -60, height: 70, background: colors.surface, transform: 'rotate(-6deg)' }),
        stripe({ bottom: -40, height: 60, background: colors.surface, transform: 'rotate(-6deg)' }),
        stripe({ bottom: 30, height: 14, background: colors.accent, transform: 'rotate(-6deg)' }),
      ];
    case 'retro':
      return [
        arc(1296, mixHex(colors.text, colors.ink, 0.7), 900),
        arc(1316, colors.surface, 900),
        arc(1334, colors.accent, 900),
      ];
    case 'handmade':
      return [
        {
          position: 'absolute',
          left: '-5%',
          right: '-5%',
          top: 1300,
          height: 120,
          background: colors.surface,
          transform: 'rotate(-3deg)',
          clipPath: TORN_TOP_PATH,
        },
      ];
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/**
 * The ornaments of the lyric card (`LyricFrame.dc.html`, `.lc-deco`): they live in the top and the
 * bottom margins and the side strips. Clean: a hairline on the top edge of the column. Hype: surface
 * stripes at -4° across the top and the bottom edge, an accent line on the bottom stripe. Noir: one
 * accent line in the top margin. Retro: three arcs from the bottom edge. Handmade: the torn strip
 * on the bottom edge.
 */
function lyricCardDeco(themeId: string, colors: StoryColours): StoryCss[] {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'clean':
      return [
        { position: 'absolute', left: 150, right: 150, top: 63, height: 2, background: storyAlpha(colors.text, 0.16) },
      ];
    case 'hype':
      return [
        stripe({ top: -64, height: 76, background: colors.surface, transform: 'rotate(-4deg)' }),
        stripe({ bottom: -46, height: 64, background: colors.surface, transform: 'rotate(-4deg)' }),
        stripe({ bottom: 18, height: 12, background: colors.accent, transform: 'rotate(-4deg)' }),
      ];
    case 'dark':
      return [{ position: 'absolute', left: 470, width: 140, top: 32, height: 3, background: colors.accent }];
    case 'retro':
      return [
        arc(1030, mixHex(colors.text, colors.ink, 0.7), 900),
        arc(1048, colors.surface, 900),
        arc(1064, colors.accent, 900),
      ];
    case 'handmade':
      return [
        {
          position: 'absolute',
          left: '-5%',
          right: '-5%',
          top: 1040,
          height: 90,
          background: colors.surface,
          transform: 'rotate(-2deg)',
          clipPath: TORN_TOP_PATH,
        },
      ];
    case 'dreamy':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/**
 * The ornaments of the covered bands (`.st-deco`): only the ground, the texture and these ornaments
 * go above y 250 and under y 1580. Hype: two surface stripes and an accent stripe at -6°. Retro:
 * three arcs in the bottom band. Handmade: the torn strip in the bottom band. A feed post has its own
 * ornaments (`feedDeco`).
 */
export function storyDeco(themeId: string, colors: StoryColours, formatId: StoryFormatId = 'story'): StoryCss[] {
  if (formatId === 'lyric11') {
    return lyricCardDeco(themeId, colors);
  }
  // A 9:16 short video keeps the ornaments of the Story bands (designs `VideoFrame`, `LyricMomentFrame`).
  if (formatId !== 'story' && formatId !== 'video916' && formatId !== 'moment916') {
    return feedDeco(themeId, colors);
  }
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return [
        stripe({ top: 110, height: 70, background: colors.surface, transform: 'rotate(-6deg)' }),
        stripe({ bottom: 120, height: 120, background: colors.surface, transform: 'rotate(-6deg)' }),
        stripe({ bottom: 60, height: 26, background: colors.accent, transform: 'rotate(-6deg)' }),
      ];
    case 'retro':
      return [arc(1640, mixHex(colors.text, colors.ink, 0.7)), arc(1730, colors.surface), arc(1820, colors.accent)];
    case 'handmade':
      return [
        {
          position: 'absolute',
          left: '-5%',
          right: '-5%',
          top: 1640,
          height: 170,
          background: colors.surface,
          transform: 'rotate(-3deg)',
          clipPath: TORN_STRIP_PATH,
        },
      ];
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The frame of the cover of each theme (`.cov`): the outer box and the picture. */
export function storyCoverFrame(
  themeId: string,
  small: boolean,
  colors: StoryColours,
  unit = 1
): { outer: StoryCss; picture: StoryCss; tape: StoryCss | null } {
  const id = storyThemeIdOf(themeId);
  const px = (value: number): number => u(value, unit);
  switch (id) {
    case 'hype':
      return {
        outer: { transform: 'rotate(5deg)' },
        picture: {
          border: `${px(14)}px solid ${colors.accent}`,
          boxShadow: `${px(22)}px ${px(22)}px 0 ${colors.surface}`,
        },
        tape: null,
      };
    case 'dreamy':
      return {
        outer: {},
        picture: {
          borderRadius: px(small ? 32 : 64),
          boxShadow: `0 ${px(50)}px ${px(120)}px -${px(30)}px ${storyAlpha(colors.accent, 0.7)}`,
        },
        tape: null,
      };
    case 'dark':
      return {
        outer: { padding: px(18), background: colors.surface, outline: `2px solid ${storyAlpha(colors.text, 0.22)}` },
        picture: { filter: 'grayscale(.85) contrast(1.05)' },
        tape: null,
      };
    case 'warm':
      return {
        outer: {},
        picture: {
          borderRadius: small ? `999px 999px ${px(12)}px ${px(12)}px` : `999px 999px ${px(24)}px ${px(24)}px`,
          boxShadow: `0 ${px(40)}px ${px(90)}px -${px(30)}px rgba(0,0,0,.6)`,
        },
        tape: null,
      };
    case 'retro':
      return {
        outer: {},
        picture: {
          border: `${px(14)}px solid ${colors.text}`,
          boxShadow: `0 ${px(30)}px ${px(60)}px -${px(30)}px rgba(0,0,0,.6)`,
        },
        tape: null,
      };
    case 'neon':
      return {
        outer: {
          padding: px(small ? 8 : 14),
          border: `${small ? 4 : 6}px solid ${colors.accent}`,
          borderRadius: px(small ? 22 : 34),
          boxShadow: `0 0 ${px(30)}px ${colors.accent}, inset 0 0 ${px(24)}px ${storyAlpha(colors.accent, 0.6)}`,
        },
        picture: { borderRadius: px(small ? 12 : 20) },
        tape: null,
      };
    case 'handmade':
      return {
        outer: {
          background: '#fffdf8',
          // `coverFrameHeight` of the layout reads the same paddings.
          padding: small ? `${px(10)}px ${px(10)}px ${px(34)}px` : `${px(20)}px ${px(20)}px ${px(72)}px`,
          transform: 'rotate(-4deg)',
          boxShadow: `0 ${px(30)}px ${px(50)}px -${px(25)}px rgba(0,0,0,.45)`,
        },
        picture: {},
        tape: {
          position: 'absolute',
          zIndex: 2,
          top: -px(small ? 16 : 30),
          left: '30%',
          width: '40%',
          height: px(small ? 32 : 60),
          background: storyAlpha(colors.surface, 0.75),
          transform: 'rotate(3deg)',
        },
      };
    case 'clean':
      return {
        outer: {},
        picture: { borderRadius: px(20), boxShadow: `0 ${px(40)}px ${px(70)}px -${px(30)}px rgba(0,0,0,.45)` },
        tape: null,
      };
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The record behind a Retro sleeve (`.t-retro .cov .rec`): it ends inside the frame, because the sleeve moved left. */
export function storyRecord(colors: StoryColours, unit = 1): StoryCss {
  return {
    position: 'absolute',
    zIndex: 0,
    top: '5%',
    right: '-34%',
    width: '90%',
    aspectRatio: '1 / 1',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${colors.text} 0 9%, ${colors.ink} 9% 16%, #1a1214 16.5% 100%)`,
    boxShadow: `inset 0 0 0 ${u(30, unit)}px #221a1c, 0 ${u(20, unit)}px ${u(40, unit)}px -${u(20, unit)}px rgba(0,0,0,.6)`,
  };
}

/** The shadow of the display text of a theme: Retro a hard accent shadow, Neon a glow. */
export function storyDisplayShadow(themeId: string, colors: StoryColours, unit = 1): string | undefined {
  const id = storyThemeIdOf(themeId);
  if (id === 'retro') {
    return `${u(7, unit)}px ${u(7, unit)}px 0 ${colors.accent}`;
  }
  if (id === 'neon') {
    return `0 0 14px currentColor, 0 0 ${u(46, unit)}px currentColor`;
  }
  return undefined;
}

/** The colour and the shadow of the hero text (date, hero word, day number). */
export function storyHeroStyle(themeId: string, frame: StoryFrame, colors: StoryColours, unit = 1): StoryCss {
  const shadow =
    storyThemeIdOf(themeId) === 'hype'
      ? `${u(10, unit)}px ${u(10, unit)}px 0 ${colors.surface}`
      : storyDisplayShadow(themeId, colors, unit);
  return { color: frame.hero === 'accent' ? colors.accent : colors.text, textShadow: shadow };
}

/** The "now word" of the lyric (`lyricMotion.nowWord` of the theme, as the design draws it on a still). */
export function storyNowWordStyle(themeId: string, colors: StoryColours, unit = 1): StoryCss {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return {
        background: colors.surface,
        color: colors.accent,
        padding: '0 .12em',
        display: 'inline-block',
        transform: 'rotate(-3deg)',
      };
    case 'dark':
      return {
        color: colors.text,
        textDecoration: 'underline',
        textDecorationColor: colors.accent,
        textDecorationThickness: 6,
        textUnderlineOffset: u(16, unit),
      };
    case 'retro':
      return { color: colors.accent, textShadow: `${u(7, unit)}px ${u(7, unit)}px 0 ${colors.text}` };
    case 'neon':
      return { color: colors.accent, textShadow: `0 0 14px ${colors.accent}, 0 0 ${u(46, unit)}px ${colors.accent}` };
    case 'handmade':
      return {
        color: colors.text,
        background: `linear-gradient(transparent 55%, ${storyAlpha(colors.accent, 0.5)} 55% 90%, transparent 90%)`,
      };
    case 'clean':
    case 'dreamy':
    case 'warm':
      return { color: colors.accent };
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/**
 * The lyric text: the text colour, the display shadow of the theme (Hype has none on the lyric). With
 * `keepCase` (the lyric card, owner answer 3 of page k) the lyric keeps the artist's own case.
 */
export function storyLyricStyle(
  themeId: string,
  frame: StoryFrame,
  colors: StoryColours,
  unit = 1,
  keepCase = false
): StoryCss {
  return {
    color: colors.text,
    textShadow: storyThemeIdOf(themeId) === 'hype' ? undefined : storyDisplayShadow(themeId, colors, unit),
    ...(frame.lyricTracking === null ? {} : { letterSpacing: `${frame.lyricTracking}em` }),
    ...(keepCase ? { textTransform: 'none' } : {}),
  };
}

/** The marker under the Handmade title (`.t-handmade .title`), on each line. Empty for every other theme. */
export function storyTitleLineStyle(themeId: string, colors: StoryColours, markerDraw = 1): StoryCss {
  if (storyThemeIdOf(themeId) !== 'handmade') {
    return { display: 'inline-block' };
  }
  const draw = Math.min(1, Math.max(0, markerDraw));
  return {
    display: 'inline-block',
    transform: 'rotate(-4deg)',
    background: `linear-gradient(transparent 60%, ${storyAlpha(colors.accent, 0.45)} 60% 92%, transparent 92%)`,
    // The marker line draws in from the left (design o, Handmade at the cut): `markerDraw` 0 to 1.
    ...(draw < 1 ? { backgroundSize: `${Math.round(draw * 1000) / 10}% 100%`, backgroundRepeat: 'no-repeat' } : {}),
  };
}

/** The kicker chip: plain text, the Hype label or the Neon tube box. */
export function storyKickerStyle(frame: StoryFrame, colors: StoryColours, unit = 1): StoryCss {
  if (frame.kicker === 'label') {
    return {
      background: colors.surface,
      color: colors.accent,
      padding: `${u(10, unit)}px ${u(30, unit)}px ${u(8, unit)}px`,
      transform: 'rotate(-5deg)',
    };
  }
  if (frame.kicker === 'tube') {
    return {
      padding: `${u(12, unit)}px ${u(30, unit)}px`,
      border: `4px solid ${colors.accent}`,
      borderRadius: 14,
      color: colors.accent,
      boxShadow: `0 0 22px ${colors.accent}, inset 0 0 14px ${storyAlpha(colors.accent, 0.5)}`,
      textShadow: `0 0 12px ${colors.accent}`,
    };
  }
  return { color: colors.text };
}

/** The date ring of a countdown (`.when`): Hype a filled label, Neon a glow, else a 4 px ring. */
export function storyWhenStyle(themeId: string, colors: StoryColours, unit = 1): StoryCss {
  const base: StoryCss = {
    color: colors.text,
    padding: `${u(14, unit)}px ${u(34, unit)}px`,
    border: '4px solid currentColor',
    borderRadius: 999,
  };
  const id = storyThemeIdOf(themeId);
  if (id === 'hype') {
    return { ...base, background: colors.surface, color: colors.accent, borderColor: colors.surface };
  }
  if (id === 'neon') {
    return { ...base, boxShadow: '0 0 22px currentColor', textShadow: '0 0 12px currentColor' };
  }
  return base;
}

/** The CTA pill: its background, its text colour, the Neon tube and the Handmade turn. */
export function storyPillStyle(themeId: string, frame: StoryFrame, colors: StoryColours): StoryCss {
  const handmade = storyThemeIdOf(themeId) === 'handmade' ? { transform: 'rotate(-2deg)' } : {};
  switch (frame.pill) {
    case 'surface':
      return { background: colors.surface, color: colors.accent, ...handmade };
    case 'text':
      return { background: colors.text, color: colors.ink, ...handmade };
    case 'tube':
      return {
        background: 'transparent',
        color: colors.accent,
        border: `6px solid ${colors.accent}`,
        boxShadow: `0 0 34px ${colors.accent}, inset 0 0 20px ${storyAlpha(colors.accent, 0.5)}`,
        textShadow: `0 0 14px ${colors.accent}`,
        ...handmade,
      };
    case 'accent':
      return { background: colors.accent, color: colors.onAccent, ...handmade };
    default: {
      const exhaustive: never = frame.pill;
      return exhaustive;
    }
  }
}

/** The ornament of an out-now Story: the hairline, the Hype bar, the Noir line. */
export function storyOrnamentColour(frame: StoryFrame, colors: StoryColours): string {
  if (frame.ornament === 'hairline') {
    return storyAlpha(colors.text, 0.16);
  }
  return frame.ornament === 'bar' ? colors.surface : colors.accent;
}
